import React from 'react'
import {
  Box,
  P,
  IconContainer,
  H3,
  Tabs,
  NavLink,
  Icon,
  Chip,
  Button,
} from '@allied-solutions/affinity'
import {
  Synced,
  Shield01,
  Shield02,
  ArrowRight,
} from '@allied-solutions/affinity-react-icons/standard'
import styled, { css } from 'styled-components'
import { useWindowWidth } from '../../hooks'
import {
  getSingleAccountChipCount,
  getSingleAccountChipVariant,
} from '../../hooks/useGetCoverageRequirements'
import { theme } from '../../theme'
import { getRequiredCoverageTypeLabel } from '../../utils/requiredCoverageTypeLabels'
import { DashboardTileStatusCard } from './DashboardTileStatusCard'

const TabLabel = styled.span`
  min-width: 0;
  white-space: normal;

  ${({ theme: t }) => t.mediaQueries.md} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const StyledNav = styled(Tabs.Nav)`
  display: flex;
  flex-wrap: nowrap;
  padding-bottom: ${props => props.theme.space['5xl']};
  border-bottom: none;

  &&& li + li {
    margin-left: 0;
  }
`

const StyledNavItem = styled(Tabs.NavItem)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;

  && {
    margin: 0;
    margin-left: 0;
  }

  ${NavLink} {
    padding: ${props => `${props.theme.space['lg']}`};
    border-radius: ${props => props.theme.radii[1]};
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    gap: ${props => props.theme.space['md']};

    &:after {
      display: none;
    }
  }
  ${Icon} {
    color: ${props => props.theme.colors.icons.gray};
  }

  ${props =>
    props.active &&
    css`
      ${NavLink} {
        background-color: ${props => props.theme.colors.background.grayMuted};
        color: ${props => props.theme.colors.text.brand};

        & ${Icon} {
          color: ${props => props.theme.colors.icons.brand};
        }
      }
    `}
`

const IconWithBadgeWrapper = styled.span`
  position: relative;
  display: inline-flex;
`

const ErrorBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors?.error};
  border: 2px solid white;
  box-sizing: border-box;
`

const StyledMobileNavItem = styled(Tabs.NavItem)`
  flex: 1;
  min-width: 0;

  ${NavLink} {
    padding: ${props => `${props.theme.space['2xl']}`};
    border-radius: ${props => props.theme.radii[1]};
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: ${props => props.theme.space['md']};
    text-align: center;

    &:after {
      display: none;
    }
  }

  ${Icon} {
    color: ${props => props.theme.colors.icons.gray};
  }

  ${props =>
    props.active &&
    css`
      ${NavLink} {
        background-color: ${props => props.theme.colors.background.grayMuted};
        color: ${props => props.theme.colors.text.brand};

        & ${Icon} {
          color: ${props => props.theme.colors.icons.brand};
        }
      }
    `}
`

const StyledTabsContent = styled(Tabs.Content)`
  padding: 0;
`

// Resolving issue of page jumping when switching tabs
const preserveScrollAfterTabChange = () => {
  const x = window.scrollX
  const y = window.scrollY
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo(x, y)
      }
    })
  })
}

const replaceStatusPlaceholders = (text, { errorCount } = {}) => {
  if (text == null || typeof text !== 'string') return text
  return text.replaceAll('{{errorCount}}', String(errorCount ?? 0))
}

const getAccountErrorCount = account =>
  account.CoverageRequirements?.filter(req => req.Status === false).length ?? 0

const getVerificationStatusMessage = account => {
  if (!account?.accountStatusDetails) return null
  const { accountStatusDetails } = account
  const message =
    accountStatusDetails.statusMessage ?? accountStatusDetails.cardMessage
  return replaceStatusPlaceholders(message, {
    errorCount: getAccountErrorCount(account),
  })
}

const VerificationAccountPanel = ({
  account,
  accountIndex,
  onNavigateToCoverageRequirements,
}) => {
  const statusMessage = getVerificationStatusMessage(account)
  return (
    <Box display="flex" flexDirection="column" gap="4xl">
      <DashboardTileStatusCard
        accountStatusDetails={account.accountStatusDetails}
        EffectiveDate={account.EffectiveDate}
      />
      <P typeStyle="labelLarge.regular" color="text.secondary" mb="0">
        {statusMessage}
      </P>
      <Box width="fit-content">
        <Button
          icon={Shield02}
          onClick={() => onNavigateToCoverageRequirements?.(accountIndex)}
          outlined
          size="md"
        >
          <Box display="flex" alignItems="center" gap="md">
            Review Requirements
            <Icon src={ArrowRight} />
          </Box>
        </Button>
      </Box>
    </Box>
  )
}

export const DashboardTileVerificationStatus = ({
  coverageRequirementsData,
  onNavigateToCoverageRequirements,
  onActiveCoverageAccountChange,
}) => {
  const statusArray = Array.isArray(coverageRequirementsData)
    ? coverageRequirementsData
    : []
  const isMobile = useWindowWidth() < Number.parseInt(theme.breakpoints[1])

  const handleCoverageTabClick = (accountIndex, referenceId) => {
    preserveScrollAfterTabChange()
    onActiveCoverageAccountChange?.(accountIndex, referenceId)
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap="lg" mb="4xl">
        <IconContainer icon={Synced} color="brand" />
        <H3 typeStyle="titleSmall" textAlign="left" fontWeight="700" mb="0">
          Verification Status
        </H3>
      </Box>
      <Box
        p="4xl"
        border="1px solid"
        borderColor="border.defaultLight"
        borderRadius="2"
        className="loan-information-box"
      >
        {statusArray.length > 1 ? (
          <Tabs idPrefix="Coverage__Requirements__Tabs">
            {({ active }) => (
              <>
                <StyledNav>
                  {statusArray.map((account, index) => {
                    const slug = String(index)
                    const accountChipCount = getSingleAccountChipCount(account)
                    const chipVariant = getSingleAccountChipVariant(account)
                    const coverageTypeLabel = getRequiredCoverageTypeLabel(
                      account.RequiredCoverageType
                    )
                    return isMobile ? (
                      <StyledMobileNavItem
                        key={account.ReferenceID ?? slug}
                        active={active === slug}
                        slug={slug}
                        typeStyle="labelMedium"
                        onClick={() =>
                          handleCoverageTabClick(index, account.ReferenceID)
                        }
                      >
                        <IconWithBadgeWrapper>
                          <Icon src={Shield01} />
                          {accountChipCount > 0 && <ErrorBadge />}
                        </IconWithBadgeWrapper>
                        <TabLabel title={coverageTypeLabel}>
                          {coverageTypeLabel}
                        </TabLabel>
                      </StyledMobileNavItem>
                    ) : (
                      <StyledNavItem
                        key={account.ReferenceID ?? slug}
                        active={active === slug}
                        slug={slug}
                        typeStyle="labelMedium"
                        onClick={() =>
                          handleCoverageTabClick(index, account.ReferenceID)
                        }
                      >
                        <TabLabel title={coverageTypeLabel}>
                          {coverageTypeLabel}
                        </TabLabel>
                        {accountChipCount > 0 && (
                          <Chip
                            label={accountChipCount}
                            rounded
                            variant={chipVariant}
                          />
                        )}
                      </StyledNavItem>
                    )
                  })}
                </StyledNav>
                <StyledTabsContent>
                  {statusArray.map((account, index) => {
                    const slug = String(index)
                    return (
                      <Tabs.Pane
                        key={account.ReferenceID ?? slug}
                        slug={slug}
                        active={index === 0}
                      >
                        <VerificationAccountPanel
                          account={account}
                          accountIndex={index}
                          onNavigateToCoverageRequirements={
                            onNavigateToCoverageRequirements
                          }
                        />
                      </Tabs.Pane>
                    )
                  })}
                </StyledTabsContent>
              </>
            )}
          </Tabs>
        ) : (
          <StyledTabsContent>
            {statusArray.map((account, index) => (
              <VerificationAccountPanel
                key={account.ReferenceID ?? String(index)}
                account={account}
                accountIndex={index}
                onNavigateToCoverageRequirements={
                  onNavigateToCoverageRequirements
                }
              />
            ))}
          </StyledTabsContent>
        )}
      </Box>
    </>
  )
}
